import React, { useEffect, useState } from 'react';

const RenaissanceClock = ({ time, progress }) => {
 const [scale, setScale] = useState(1);
 
 useEffect(() => {
   const handleResize = () => {
     const width = window.innerWidth;
     const breakpoint = 1332;
     const minScale = 0.72; // Smaller scale for mobile
     
     if (width < 640) { // Mobile breakpoint
       const mobileScale = Math.max(minScale, Math.min(0.65, width / 1000));
       setScale(mobileScale);
     } else if (width < breakpoint) {
       const newScale = Math.max(0.7, Math.min(0.9, width / breakpoint));
       setScale(newScale);
     } else {
       setScale(0.9);
     }
   };

   window.addEventListener('resize', handleResize);
   handleResize();
   
   return () => window.removeEventListener('resize', handleResize);
 }, []);

 return (
    <div className="fixed top-2 sm:top-4 md:top-8 right-2 sm:right-4 md:right-8 text-amber-100/90" style={{ 
      transform: `scale(${scale})`,
      transformOrigin: 'top right',
      transition: 'transform 0.3s ease-out',
      filter: "drop-shadow(0 0 10px rgba(212, 175, 55, 0.1))"
    }}>
     <div className="relative backdrop-blur-lg bg-amber-900/30 p-4 sm:p-6 rounded-lg border-2 border-amber-700/50 shadow-xl">
       <div className="absolute inset-0 overflow-hidden">
         <div className="absolute -top-3 -left-3 w-6 h-6 border-2 border-amber-600/50 rotate-45 bg-amber-900/80" />
         <div className="absolute -top-3 -right-3 w-6 h-6 border-2 border-amber-600/50 rotate-45 bg-amber-900/80" />
         <div className="absolute -bottom-3 -left-3 w-6 h-6 border-2 border-amber-600/50 rotate-45 bg-amber-900/80" />
         <div className="absolute -bottom-3 -right-3 w-6 h-6 border-2 border-amber-600/50 rotate-45 bg-amber-900/80" />
         
         <div className="absolute inset-0 border-[3px] border-amber-700/30 rounded-lg" />
         <div className="absolute inset-2 border-[1px] border-amber-700/20 rounded-md" />
       </div>

       <div className="relative z-10">
         <div className="text-center mb-4 sm:mb-6">
           <div style={{ fontFamily: "'Playfair Display', serif" }} 
                className="text-3xl sm:text-4xl font-medium mb-1 text-amber-50 tracking-wide transition-colors duration-300">
             {time.toLocaleTimeString('en-US', { hour12: false })}
           </div>
           <div style={{ fontFamily: "'Lora', serif" }} 
                className="text-sm sm:text-base text-amber-200/80 uppercase tracking-wider border-t border-amber-700/30 pt-2 font-medium">
             {time.toLocaleDateString('en-US', { 
               weekday: 'long', 
               year: 'numeric', 
               month: 'long', 
               day: 'numeric' 
             })}
           </div>
         </div>

         <div className="flex justify-center items-center gap-2 sm:gap-4">
           {['Day', 'Month', 'Year'].map((label, index) => (
             <React.Fragment key={label}>
               <div className="relative">
                 <div className="relative w-16 h-16 sm:w-20 sm:h-20 cursor-pointer transition-transform duration-300">
                   <svg viewBox="0 0 100 100" className="absolute inset-0">
                     <circle
                       cx="50"
                       cy="50"
                       r="45"
                       fill="none"
                       stroke="#3A2E1D"
                       strokeWidth="8"
                     />
                     <circle
                       cx="50"
                       cy="50"
                       r="45"
                       fill="none"
                       stroke="#D4AF37"
                       strokeWidth="6"
                       strokeDasharray={`${Math.PI * 90}`}
                       strokeDashoffset={`${Math.PI * 90 * (1 - progress[label.toLowerCase()])}`}
                       transform="rotate(-90 50 50)"
                       className="transition-all duration-300"
                     />
                     <circle cx="50" cy="50" r="42" fill="#2B2116" />
                   </svg>

                   <div className="absolute inset-0 flex items-center justify-center">
                     <div className="text-center">
                       <div style={{ fontFamily: "'Lora', serif" }} 
                            className="text-base sm:text-xl text-amber-200 font-medium">
                         {(progress[label.toLowerCase()] * 100).toFixed(0)}%
                       </div>
                       <div style={{ fontFamily: "'Cormorant Garamond', serif" }} 
                            className="text-[10px] sm:text-xs uppercase tracking-wider text-amber-400/80 font-bold">
                         {label}
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
               {index < 2 && (
                 <span className="text-amber-700/50 font-serif text-lg">•</span>
               )}
             </React.Fragment>
           ))}
         </div>

         <div className="mt-4 sm:mt-6 text-center">
           <div className="relative inline-block px-3 sm:px-4 py-1 sm:py-2">
             <div className="absolute inset-0 border-t border-b border-amber-700/30" />
             <div style={{ fontFamily: "'Cormorant Garamond', serif" }} 
                  className="italic text-sm sm:text-xl text-amber-200 tracking-wider font-semibold">
               Tempus Fugit 𐌏 Time Flies
             </div>
           </div>
         </div>
       </div>
     </div>
   </div>
 );
};

export default RenaissanceClock;